/**
 * Admin · Stations (§21, §18 / §B-7). MANAGER sees the read-only table; ADMIN
 * creates / edits / deletes stations and manages the §18 trust lifecycle (enrol
 * mints a trust window from Settings.stationTrustDays; revoke is the remote kill).
 * Writes go through the ADMIN API (audited, optimistic concurrency).
 */
import { useCallback, useMemo, useState } from "react";
import { ApiError, api, type AdminStation } from "../../lib/api.js";
import { useResource } from "../../lib/useResource.js";
import { useSession } from "../../components/auth.js";
import { formatAge } from "../../lib/age.js";
import { StateChip, DataTable, type Column, Button, EmptyState, ErrorState, SkeletonRows, useToast } from "../../components/ui/index.js";
import { StationEditor } from "./stations/StationEditor.js";

const TRUST_CHIP: Record<string, string> = {
  trusted: "chip--ready",
  expired: "chip--hold",
  revoked: "chip--exception",
  untrusted: "chip",
};
function trustChip(s: AdminStation) {
  const status = s.trustStatus ?? "untrusted";
  const cls = TRUST_CHIP[status] ?? "chip";
  const label = status === "trusted" && s.trustExpiresAt ? `trusted · ${formatAge(s.trustExpiresAt)}` : status;
  return <span className={`chip ${cls}`}>{label}</span>;
}

export function Stations() {
  const { user } = useSession();
  const isAdmin = user?.role === "ADMIN";
  const r = useResource(api.admin.stations);
  const { toast } = useToast();
  const [editing, setEditing] = useState<AdminStation | null | "new">(null);
  const [busy, setBusy] = useState(false);

  const reload = r.reload;
  const stations = useMemo(() => r.data?.stations ?? [], [r.data]);

  const act = useCallback(
    async (fn: () => Promise<unknown>, okMsg: string) => {
      setBusy(true);
      try {
        await fn();
        toast(okMsg, "success");
        reload();
      } catch (e) {
        // §C — a 409 means the station changed under us (e.g. a concurrent revoke).
        toast(e instanceof ApiError && e.status === 409 ? "Station changed elsewhere — reloading." : "Action failed.", "danger");
        reload();
      } finally {
        setBusy(false);
      }
    },
    [reload, toast],
  );

  const remove = useCallback(
    (s: AdminStation) => {
      if (!window.confirm(`Delete station “${s.name}”?`)) return;
      void act(() => api.admin.deleteStation(s.id), "Station deleted");
    },
    [act],
  );

  const columns = useMemo<Array<Column<AdminStation>>>(() => {
    const base: Array<Column<AdminStation>> = [
      { key: "name", header: "Station", cell: (s) => <span className="cell-strong">{s.name}</span> },
      { key: "printer", header: "Printer", cell: (s) => (s.printerConfigured ? <StateChip on onLabel="set" offLabel="" /> : <span className="muted">—</span>) },
      { key: "scale", header: "Scale", cell: (s) => (s.scaleConfigured ? <StateChip on onLabel="set" offLabel="" /> : <span className="muted">—</span>) },
      { key: "trust", header: "Trust", cell: (s) => trustChip(s) },
      { key: "active", header: "Status", cell: (s) => <StateChip on={s.active} onLabel="Active" offLabel="Inactive" /> },
    ];
    if (!isAdmin) return base;
    return [
      ...base,
      {
        key: "actions",
        header: "",
        cell: (s) => (
          <span className="row-actions">
            {s.trustStatus === "trusted" ? (
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => void act(() => api.admin.revokeStation(s.id, undefined, s.updatedAt), "Trust revoked")}>Revoke</Button>
            ) : (
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => void act(() => api.admin.enrollStation(s.id, s.updatedAt), "Station enrolled")}>Enrol</Button>
            )}
            <Button variant="ghost" size="sm" icon="edit" iconOnly aria-label={`Edit ${s.name}`} onClick={() => setEditing(s)} />
            <Button variant="ghost" size="sm" icon="x" iconOnly aria-label={`Delete ${s.name}`} disabled={busy} onClick={() => remove(s)} />
          </span>
        ),
      },
    ];
  }, [isAdmin, busy, act, remove]);

  return (
    <section className="admin">
      <div className="page-head">
        <div className="page-head__title">
          <h1>Stations</h1>
          {r.data && <span className="page-head__count">{stations.length} stations</span>}
        </div>
        {isAdmin ? (
          <div className="page-head__actions">
            <Button variant="primary" size="sm" icon="plus" onClick={() => setEditing("new")}>Add station</Button>
          </div>
        ) : (
          <span className="soon" title="Admin role required to edit">Read-only · admin can edit</span>
        )}
      </div>

      {r.error ? (
        <ErrorState title="Couldn’t load this" hint="Retry in a moment." onRetry={r.reload} />
      ) : r.loading && !r.data ? (
        <div className="table-card"><SkeletonRows rows={4} cols={isAdmin ? 6 : 5} /></div>
      ) : stations.length === 0 ? (
        <EmptyState icon="box" title="No stations yet" hint={isAdmin ? "Add a pack station, then enrol it to grant trust." : "Stations appear once enrolled."} />
      ) : (
        <div aria-busy={busy}>
          <DataTable ariaLabel="Stations" rows={stations} getRowKey={(s) => s.id} columns={columns} />
        </div>
      )}

      {editing !== null && (
        <StationEditor
          station={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </section>
  );
}
